import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import type { RunProgress } from "@atomic/allocation-adapter";
import { MODULE_CODE } from "../containers/ReplenishmentView/constants";
import { AllocationRunsDocument } from "../services/__generated__/graphql";
import { apolloClient } from "../services/backend_gateway/apollo-client";
import {
  WORKFLOW_ID,
  type ReceiptValidity,
  type ReceiptValidityByRunId,
  resolveSelectedRun,
  resolveSelectedRunId,
  sortCompletedWorkflowRuns,
  sortWorkflowRuns,
} from "./runSelectionState";

/** How many of the module's runs the selector offers. */
const RUN_LIST_LIMIT = 50;
/** There is no run subscription any more, so the list is re-read on a timer
 *  while the app is open, on focus, and on demand when a run this page started
 *  finishes (useRunEngine). */
const RUN_LIST_REFRESH_MS = 30_000;

/** One engine run as core's ledger lists it (`allocationRuns`). The shape is
 *  core's; only the fields this app reads are named. */
export interface CoreRunRow {
  run_id: number;
  process_run_id?: number | null;
  run_number?: number | null;
  status: string;
  start_datetime: string | null;
  runtime_ms?: number | null;
  triggered_by?: string | null;
  module_code?: string;
  progress?: RunProgress | null;
}

/** A run as the selector, the cube and the run history read it. */
export interface RunRow {
  /** `String(runId)` — the selector keys on strings. */
  id: string;
  /** Core's module-scoped run number: what the cube reads are keyed by. */
  runId: number;
  /** The run's start, from the ledger. */
  createdAt: string;
  /** The selector's vocabulary: completed, failed, or still going. */
  status: "RUN_COMPLETED" | "RUN_FAILED" | "RUN_IN_PROGRESS";
  /** Core's own word for it (`succeeded`, `running`, `failed`, …). */
  coreStatus: string;
  /** Core runs carry no editable name. */
  name: null;
  runSettings: { workflowId: string };
  runtimeMs: number | null;
  triggeredBy: string | null;
  progress: RunProgress | null;
}

export function selectorStatus(coreStatus: string): RunRow["status"] {
  if (coreStatus === "succeeded") return "RUN_COMPLETED";
  if (coreStatus === "failed") return "RUN_FAILED";
  return "RUN_IN_PROGRESS";
}

export function toRunRow(row: CoreRunRow): RunRow {
  return {
    id: String(row.run_id),
    runId: row.run_id,
    createdAt: row.start_datetime ?? "",
    status: selectorStatus(row.status),
    coreStatus: row.status,
    name: null,
    runSettings: { workflowId: WORKFLOW_ID },
    runtimeMs: row.runtime_ms ?? null,
    triggeredBy: row.triggered_by ?? null,
    progress: row.progress ?? null,
  };
}

/** The module's recent runs, through the gateway. A JSON string on the wire
 *  because the row shape is core's. */
export async function fetchCoreRuns(
  limit = RUN_LIST_LIMIT,
  moduleCode: string = MODULE_CODE
): Promise<CoreRunRow[]> {
  const { data } = await apolloClient.mutate({
    mutation: AllocationRunsDocument,
    variables: { moduleCode, limit },
    fetchPolicy: "no-cache",
  });
  return JSON.parse(data?.allocationRuns ?? "[]") as CoreRunRow[];
}

export interface RunSelectionContextValue {
  /** Whose runs these are: a Run started under this provider is this module's. */
  moduleCode: string;
  selectedRun: RunRow | null;
  selectedRunId: string | null;
  setSelectedRunId: Dispatch<SetStateAction<string | null>>;
  /** Completed runs, newest first — what the selector offers. */
  sortedRuns: RunRow[];
  /** Every run, newest first, including failed and in-flight ones. */
  visibleRuns: RunRow[];
  receiptValidityByRunId: ReceiptValidityByRunId;
  reportReceiptValidity: (runId: string, validity: Exclude<ReceiptValidity, "unknown">) => void;
  loadingRuns: boolean;
  /** Re-read the list now — for a run this app started and watched to the end,
   *  so the selector can move to it without waiting for the next tick. */
  refetchRuns: () => Promise<void>;
}

const RunSelectionContext = createContext<RunSelectionContextValue | null>(null);

/** `moduleCode` picks whose runs are listed: Replenishment app-wide, and a page
 *  planning another module (MRP) nests its own provider, which wins inside it. */
export function RunSelectionProvider({
  children,
  moduleCode = MODULE_CODE,
}: {
  children: ReactNode;
  moduleCode?: string;
}) {
  const [selectedRunId, setSelectedRunId] = useState<string | null>(null);
  const [receiptValidityByRunId, setReceiptValidityByRunId] = useState<
    Record<string, ReceiptValidity>
  >({});
  const [runs, setRuns] = useState<RunRow[] | null>(null);
  const mountedRef = useRef(true);

  const refetchRuns = useCallback(async () => {
    try {
      const rows = await fetchCoreRuns(RUN_LIST_LIMIT, moduleCode);
      if (mountedRef.current) setRuns(rows.map(toRunRow));
    } catch (error) {
      // A failed refresh keeps the last list on screen; the next tick retries.
      console.error("Failed to load runs", error);
      if (mountedRef.current) setRuns((current) => current ?? []);
    }
  }, [moduleCode]);

  useEffect(() => {
    mountedRef.current = true;
    void refetchRuns();
    const handle = window.setInterval(() => void refetchRuns(), RUN_LIST_REFRESH_MS);
    const onFocus = () => void refetchRuns();
    window.addEventListener("focus", onFocus);
    return () => {
      mountedRef.current = false;
      window.clearInterval(handle);
      window.removeEventListener("focus", onFocus);
    };
  }, [refetchRuns]);

  const visibleRuns = useMemo(() => sortWorkflowRuns(runs ?? [], WORKFLOW_ID), [runs]);
  const sortedRuns = useMemo(
    () => sortCompletedWorkflowRuns(visibleRuns, WORKFLOW_ID, "RUN_COMPLETED"),
    [visibleRuns]
  );

  const reportReceiptValidity = useCallback(
    (runId: string, validity: Exclude<ReceiptValidity, "unknown">) => {
      setReceiptValidityByRunId((current) =>
        current[runId] === validity ? current : { ...current, [runId]: validity }
      );
    },
    []
  );

  useEffect(() => {
    if (sortedRuns.length === 0) {
      return;
    }

    const resolvedRunId = resolveSelectedRunId(selectedRunId, sortedRuns);
    if (resolvedRunId !== selectedRunId) {
      setSelectedRunId(resolvedRunId);
    }
  }, [selectedRunId, sortedRuns]);

  const selectedRun = useMemo(
    () => resolveSelectedRun(selectedRunId, sortedRuns),
    [selectedRunId, sortedRuns]
  );

  const loadingRuns = runs === null;

  const value = useMemo(
    () => ({
      selectedRun,
      selectedRunId,
      setSelectedRunId,
      sortedRuns,
      visibleRuns,
      receiptValidityByRunId,
      reportReceiptValidity,
      loadingRuns,
      refetchRuns,
      moduleCode,
    }),
    [
      moduleCode,
      loadingRuns,
      receiptValidityByRunId,
      reportReceiptValidity,
      refetchRuns,
      selectedRun,
      selectedRunId,
      sortedRuns,
      visibleRuns,
    ]
  );

  return createElement(RunSelectionContext.Provider, { value }, children);
}

export function useRunSelection(): RunSelectionContextValue {
  const context = useContext(RunSelectionContext);
  if (!context) {
    throw new Error("useRunSelection must be used within RunSelectionProvider");
  }
  return context;
}
